import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

const { FiCalendar, FiClock, FiMapPin, FiActivity, FiX, FiTrendingUp, FiTrash2 } = FiIcons;

const HistoryModal = ({ onClose, history, stats, onClear }) => {
  
  // Grupăm istoricul pe luni/ani
  const groupedHistory = useMemo(() => {
    const groups = {};
    history.forEach(session => {
      const date = new Date(session.date);
      // Cheie de grup: "Octombrie 2023"
      const key = format(date, 'MMMM yyyy', { locale: ro }); 
      // Capitalizăm prima literă
      const formattedKey = key.charAt(0).toUpperCase() + key.slice(1);
      
      if (!groups[formattedKey]) {
        groups[formattedKey] = [];
      }
      groups[formattedKey].push(session);
    });
    return groups;
  }, [history]);

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full flex flex-col max-h-[85vh]"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
              <SafeIcon icon={FiCalendar} className="text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Istoric Activitate</h2>
              <p className="text-sm text-slate-500">Evidența progresului tău</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600"
          >
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        {/* Stats Summary Cards */}
        <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-3 bg-white shadow-sm z-10">
          <StatCard 
            icon={FiTrendingUp} 
            value={stats.totalSessions} 
            label="Sesiuni" 
            color="blue" 
          />
          <StatCard 
            icon={FiMapPin} 
            value={`${stats.totalDistance}`} 
            unit="km"
            label="Distanță" 
            color="emerald" 
          />
          <StatCard 
            icon={FiActivity} 
            value={(stats.totalSteps / 1000).toFixed(1)} 
            unit="k"
            label="Pași" 
            color="amber" 
          />
          <StatCard 
            icon={FiClock} 
            value={`${Math.floor(stats.totalTime / 60)}h ${stats.totalTime % 60}m`} 
            label="Timp Total" 
            color="indigo" 
          />
        </div>

        {/* Scrollable List */}
        <div className="overflow-y-auto flex-1 p-6 bg-slate-50">
          {history.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <SafeIcon icon={FiCalendar} className="text-4xl mx-auto mb-3 opacity-20" />
              <p>Nu ai nicio sesiune salvată încă.</p>
              <p className="text-sm">Începe un antrenament!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedHistory).map(([groupTitle, sessions]) => (
                <div key={groupTitle}>
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 ml-1">
                    {groupTitle}
                  </h3>
                  <div className="space-y-3">
                    {sessions.map((session) => (
                      <div key={session.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-center justify-between hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4">
                          <div className={`
                            w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold
                            ${session.intensity === 'intens' ? 'bg-rose-100 text-rose-600' : 
                              session.intensity === 'mediu' ? 'bg-amber-100 text-amber-600' :
                              session.intensity === 'start' ? 'bg-blue-100 text-blue-600' :
                              'bg-emerald-100 text-emerald-600'}
                          `}>
                            {session.intensity.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800 flex items-center gap-2">
                              {format(new Date(session.date), 'dd MMMM, HH:mm', { locale: ro })}
                            </div>
                            <div className="text-xs text-slate-500 capitalize">
                              {session.intensity} • {Math.floor(session.duration / 60)} min {session.duration % 60 > 0 && `${session.duration % 60} sec`}
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="font-bold text-slate-800">{session.steps} pași</div>
                          <div className="text-xs text-slate-500">{session.distance.toFixed(2)} km</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        {history.length > 0 && (
            <div className="p-4 bg-white border-t border-slate-100 flex justify-center">
                <button 
                    onClick={() => {
                        if(window.confirm('Sigur vrei să ștergi tot istoricul?')) {
                            onClear();
                        }
                    }}
                    className="text-red-500 text-xs flex items-center gap-1 hover:underline opacity-60 hover:opacity-100 transition-opacity"
                >
                    <SafeIcon icon={FiTrash2} /> Șterge tot istoricul
                </button>
            </div>
        )}

      </motion.div>
    </div>
  );
};

const StatCard = ({ icon, value, unit, label, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    indigo: 'bg-indigo-50 text-indigo-600',
  };

  return (
    <div className="bg-slate-50 rounded-2xl p-3 border border-slate-100 text-center">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2 ${colorClasses[color]}`}>
        <SafeIcon icon={icon} className="text-sm" />
      </div>
      <div className="text-lg font-bold text-slate-800 leading-none mb-1">
        {value} <span className="text-xs font-normal text-slate-500">{unit}</span>
      </div>
      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{label}</div>
    </div>
  );
};

export default HistoryModal;